// frontend/src/components/InsightsPage.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaBookOpen,
  FaChartLine,
  FaCheckCircle,
  FaDatabase,
  FaInfoCircle,
  FaListAlt,
  FaSearch,
  FaShieldAlt,
  FaTimes,
} from "react-icons/fa";
import { supabase } from "../../supabaseClient";
import "./InsightsPage.css";

export default function InsightsPage() {
  const navigate = useNavigate();

  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showWhyModal, setShowWhyModal] = useState(false);
  const [showDatabaseLookupModal, setShowDatabaseLookupModal] = useState(false);

  const [certNumber, setCertNumber] = useState("");
  const [gradingCo, setGradingCo] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const [nameLookupInput, setNameLookupInput] = useState("");
  const [nameLookupResults, setNameLookupResults] = useState([]);
  const [nameLookupLoading, setNameLookupLoading] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [databaseLookupError, setDatabaseLookupError] = useState("");

  const closeVerify = () => {
    setShowVerifyModal(false);
    setShowWhyModal(false);
    setLookupResult(null);
    setCertNumber("");
    setGradingCo("");
    setVerifyError("");
  };

  const handleLookup = async () => {
    setVerifyError("");
    const company = gradingCo.trim();
    const cert = certNumber.trim();

    if (!company || !cert) {
      setVerifyError("Please enter both grading company and cert number.");
      return;
    }

    setLookupLoading(true);
    setLookupResult(null);

    try {
      const { data, error } = await supabase.rpc("get_cert_count", {
        p_grading_company: company,
        p_cert_number: cert,
      });

      if (error) throw error;
      setLookupResult(data ?? 0);
    } catch (err) {
      console.error("Lookup failed:", err);
      setVerifyError("Hobbli could not complete that verification lookup. Please try again.");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleDatabaseLookup = async () => {
    setDatabaseLookupError("");
    const value = nameLookupInput.trim();

    if (!value) {
      setDatabaseLookupError("Please enter an item name to search.");
      return;
    }

    setNameLookupLoading(true);
    setNameLookupResults([]);

    try {
      const { data, error } = await supabase.rpc("get_name_counts", {
        p_name_search: value,
      });

      if (error) throw error;
      setNameLookupResults(data ?? []);
    } catch (err) {
      console.error("Database Counts Lookup failed:", err);
      setDatabaseLookupError("Hobbli could not complete that database lookup. Please try again.");
    } finally {
      setNameLookupLoading(false);
    }
  };

  const sections = [
    {
      key: "verify",
      eyebrow: "Collector protection",
      title: "Hobbli Verify",
      description:
        "Cross-check grading company and certification numbers against Hobbli collection data to spot unusual duplication.",
      icon: FaCheckCircle,
      action: "Open Verify",
      onClick: () => setShowVerifyModal(true),
      status: "Available",
    },
    {
      key: "guides",
      eyebrow: "Learn & discover",
      title: "Collector Guides",
      description:
        "Explore grading, authenticity, collecting strategy, hobby knowledge, and community-written educational guides.",
      icon: FaBookOpen,
      action: "Browse Guides",
      onClick: () => navigate("/articles"),
      status: "Available",
    },
    {
      key: "checklists",
      eyebrow: "Catalog research",
      title: "Checklists & Set Lookup",
      description:
        "Explore Hobbli's canonical set catalog, track sets you're building, and see what you own or still need.",
      icon: FaListAlt,
      action: "Explore Checklists",
      onClick: () => navigate("/insights/checklists"),
      status: "Available",
    },
    {
      key: "market",
      eyebrow: "Hobbli data",
      title: "Market Insights",
      description:
        "Explore how the card catalog has changed over time, with annual card counts, sports, brands, releases, and families.",
      icon: FaChartLine,
      action: "Explore Market Insights",
      onClick: () => navigate("/insights/market"),
      status: "Available",
    },
  ];

  return (
    <main className="hobbli-insights">
      <section className="hobbli-insights__hero">
        <div className="hobbli-insights__hero-copy">
          <span className="hobbli-insights__eyebrow">Hobbli Insights</span>
          <h1>Collect with more confidence.</h1>
          <p>
            Practical tools, trusted guides, and growing hobby intelligence to
            help you learn, verify, and make better collecting decisions.
          </p>

          <div className="hobbli-insights__hero-actions">
            <button
              className="hobbli-insights__primary-button"
              onClick={() => setShowVerifyModal(true)}
            >
              <FaShieldAlt /> Try Hobbli Verify
            </button>
            <button
              className="hobbli-insights__secondary-button"
              onClick={() => navigate("/articles")}
            >
              <FaBookOpen /> Explore Guides
            </button>
          </div>
        </div>

        <div className="hobbli-insights__hero-mark" aria-hidden="true">
          <FaShieldAlt />
        </div>
      </section>

      <section className="hobbli-insights__section">
        <div className="hobbli-insights__section-heading">
          <div>
            <span className="hobbli-insights__eyebrow">Explore</span>
            <h2>Tools & knowledge</h2>
          </div>
          <p>Built for collectors, powered by Hobbli.</p>
        </div>

        <div className="hobbli-insights__grid">
          {sections.map((section) => {
            const Icon = section.icon;
            const disabled = !section.onClick;

            return (
              <button
                type="button"
                key={section.key}
                className={`hobbli-insights-card ${
                  disabled ? "hobbli-insights-card--disabled" : ""
                }`}
                onClick={section.onClick || undefined}
                disabled={disabled}
              >
                <div className="hobbli-insights-card__top">
                  <span className="hobbli-insights-card__icon">
                    <Icon />
                  </span>
                  <span
                    className={`hobbli-insights-card__status ${
                      disabled ? "" : "hobbli-insights-card__status--live"
                    }`}
                  >
                    {section.status}
                  </span>
                </div>

                <div className="hobbli-insights-card__body">
                  <span className="hobbli-insights-card__eyebrow">
                    {section.eyebrow}
                  </span>
                  <h3>{section.title}</h3>
                  <p>{section.description}</p>
                </div>

                <div className="hobbli-insights-card__footer">
                  <span>{section.action}</span>
                  {!disabled && <FaArrowRight />}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="hobbli-insights__trust-strip">
        <FaInfoCircle />
        <div>
          <strong>Hobbli insights are context, not guarantees.</strong>
          <span>
            Verification tools can flag unusual patterns, but collectors should
            still use grading-company records and their own due diligence.
          </span>
        </div>
      </section>

      <div className="hobbli-insights__nav-row">
        <button onClick={() => navigate("/collection")}>← My Collection</button>
        <button onClick={() => navigate("/shop")}>Hobbli Shop →</button>
      </div>

      {showVerifyModal && !showWhyModal && (
        <div
          className="hobbli-verify-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeVerify();
          }}
        >
          <section
            className="hobbli-verify-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hobbli-verify-title"
          >
            <button
              className="hobbli-verify-dialog__close"
              onClick={closeVerify}
              aria-label="Close Hobbli Verify"
            >
              <FaTimes />
            </button>

            <div className="hobbli-verify-dialog__header">
              <span className="hobbli-verify-dialog__icon">
                <FaShieldAlt />
              </span>
              <div>
                <span className="hobbli-insights__eyebrow">Collector protection</span>
                <h2 id="hobbli-verify-title">Hobbli Verify</h2>
                <p>
                  See how many Hobbli collection records contain the same grading
                  company and certification number.
                </p>
              </div>
            </div>

            <div className="hobbli-verify-dialog__notice">
              <FaInfoCircle />
              <span>
                A duplicate result is a signal to investigate—not proof that an
                item is counterfeit.
              </span>
            </div>

            <div className="hobbli-verify-form">
              <label>
                <span>Grading company</span>
                <input
                  type="text"
                  value={gradingCo}
                  onChange={(e) => {
                    setGradingCo(e.target.value);
                    setLookupResult(null);
                  }}
                  placeholder="PSA, SGC, BGS..."
                  autoComplete="off"
                />
              </label>

              <label>
                <span>Certification number</span>
                <input
                  type="text"
                  value={certNumber}
                  onChange={(e) => {
                    setCertNumber(e.target.value);
                    setLookupResult(null);
                  }}
                  placeholder="Enter cert number"
                  autoComplete="off"
                />
              </label>
            </div>

            <button
              className="hobbli-verify-dialog__search"
              onClick={handleLookup}
              disabled={lookupLoading}
            >
              <FaSearch />
              {lookupLoading ? "Checking Hobbli..." : "Check Certification"}
            </button>

            {verifyError ? <div role="alert" className="hobbli-verify-result hobbli-verify-result--attention"><div><strong>{verifyError}</strong></div></div> : null}

            {lookupResult !== null && (
              <div
                className={`hobbli-verify-result ${
                  lookupResult > 1 ? "hobbli-verify-result--attention" : ""
                }`}
              >
                <span className="hobbli-verify-result__number">{lookupResult}</span>
                <div>
                  <strong>
                    {lookupResult === 0
                      ? "No matching Hobbli records"
                      : `${lookupResult} matching ${
                          lookupResult === 1 ? "record" : "records"
                        }`}
                  </strong>
                  <p>
                    {lookupResult === 0
                      ? `No items currently match ${gradingCo.trim()} certification ${certNumber.trim()}.`
                      : `Hobbli found this ${gradingCo.trim()} certification number in ${lookupResult} collection ${
                          lookupResult === 1 ? "record" : "records"
                        }.`}
                  </p>
                </div>
              </div>
            )}

            <div className="hobbli-verify-dialog__utility-row">
              <button
                onClick={() => {
                  setShowDatabaseLookupModal(true);
                  setNameLookupInput("");
                  setNameLookupResults([]);
                }}
              >
                <FaDatabase /> Item Counts Lookup
              </button>
              <button onClick={() => setShowWhyModal(true)}>
                <FaInfoCircle /> How does this work?
              </button>
            </div>
          </section>
        </div>
      )}

      {showVerifyModal && showWhyModal && (
        <div className="hobbli-verify-overlay">
          <section
            className="hobbli-verify-dialog hobbli-verify-dialog--info"
            role="dialog"
            aria-modal="true"
            aria-labelledby="verify-why-title"
          >
            <button
              className="hobbli-verify-dialog__close"
              onClick={() => setShowWhyModal(false)}
              aria-label="Back to Hobbli Verify"
            >
              <FaTimes />
            </button>

            <span className="hobbli-insights__eyebrow">About the tool</span>
            <h2 id="verify-why-title">Why certification checks matter</h2>

            <div className="hobbli-verify-explainer">
              <p>
                Bad actors can copy real certification numbers onto fraudulent
                items. A grading-company lookup may confirm that a certification
                number exists without telling you how many physical items are
                circulating with that number.
              </p>
              <p>
                Hobbli cross-references certification numbers represented in its
                own collection data. Repeated registrations can be useful
                context, but they do not automatically mean an item is fake.
              </p>
              <p>
                The value of these signals improves as more collectors register
                items. Always combine Hobbli Verify with grading-company records,
                photos, seller history, and your own due diligence.
              </p>
            </div>

            <button
              className="hobbli-verify-dialog__search"
              onClick={() => setShowWhyModal(false)}
            >
              Back to Verify
            </button>
          </section>
        </div>
      )}

      {showDatabaseLookupModal && (
        <div
          className="hobbli-verify-overlay hobbli-verify-overlay--top"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowDatabaseLookupModal(false);
            }
          }}
        >
          <section
            className="hobbli-verify-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="database-lookup-title"
          >
            <button
              className="hobbli-verify-dialog__close"
              onClick={() => setShowDatabaseLookupModal(false)}
              aria-label="Close item counts lookup"
            >
              <FaTimes />
            </button>

            <div className="hobbli-verify-dialog__header">
              <span className="hobbli-verify-dialog__icon">
                <FaDatabase />
              </span>
              <div>
                <span className="hobbli-insights__eyebrow">Hobbli database</span>
                <h2 id="database-lookup-title">Item Counts Lookup</h2>
                <p>
                  Search similar item names to see how many registered Hobbli
                  items currently include a certification or COA number.
                </p>
              </div>
            </div>

            <div className="hobbli-verify-form hobbli-verify-form--single">
              <label>
                <span>Item name</span>
                <input
                  type="text"
                  value={nameLookupInput}
                  onChange={(e) => {
                    setNameLookupInput(e.target.value);
                    setNameLookupResults([]);
                  }}
                  placeholder="e.g. 1989 Upper Deck Ken Griffey Jr."
                />
              </label>
            </div>

            <button
              className="hobbli-verify-dialog__search"
              onClick={handleDatabaseLookup}
              disabled={nameLookupLoading}
            >
              <FaSearch />
              {nameLookupLoading ? "Searching..." : "Search Hobbli"}
            </button>

            {databaseLookupError ? <div role="alert" className="hobbli-verify-result hobbli-verify-result--attention"><div><strong>{databaseLookupError}</strong></div></div> : null}

            {nameLookupResults.length > 0 && (
              <div className="hobbli-verify-count-results">
                {nameLookupResults.map((row) => (
                  <div key={`${row.name}-${row.count}`}>
                    <span>{row.name}</span>
                    <strong>{row.count}</strong>
                  </div>
                ))}
              </div>
            )}

            {!nameLookupLoading &&
              nameLookupInput.trim() &&
              nameLookupResults.length === 0 && (
                <p className="hobbli-verify-empty">
                  Search to view matching Hobbli registration counts.
                </p>
              )}

            <button
              className="hobbli-verify-dialog__text-button"
              onClick={() => setShowDatabaseLookupModal(false)}
            >
              ← Back to Hobbli Verify
            </button>
          </section>
        </div>
      )}
    </main>
  );
}
